import React, { useState, useEffect, useRef } from 'react';
import { TextField, Button, Box, Paper, Autocomplete, Typography, CircularProgress, Slider, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  borderRadius: theme.spacing(2),
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)'
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #4568dc 0%, #6f5de7 100%)',
  padding: theme.spacing(1.5),
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #3f5bd3 0%, #634bd8 100%)',
    transform: 'scale(1.02)'
  }
}));

const SearchForm = ({ onSearch, isSearching }) => {
  const [businessSuggestions, setBusinessSuggestions] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [searchRadius, setSearchRadius] = useState(5);
  const [loading, setLoading] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  useEffect(() => {
    const initServices = () => {
      if (window.google && !autocompleteService.current) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        placesService.current = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );
      }
    };

    if (window.google) {
      initServices();
    } else {
      // Wait for Google Maps script to load
      const checkGoogleInterval = setInterval(() => {
        if (window.google) {
          initServices();
          clearInterval(checkGoogleInterval);
        }
      }, 100);

      return () => clearInterval(checkGoogleInterval);
    }
  }, []);

  const handleBusinessInputChange = async (event, newInputValue) => {
    if (!newInputValue || !autocompleteService.current) return;

    setLoading(true);
    try {
      const predictions = await new Promise((resolve, reject) => {
        autocompleteService.current.getPlacePredictions(
          { 
            input: newInputValue, 
            types: ['establishment'],
            componentRestrictions: { country: 'in' } // Restrict to India only
          },
          (results, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(results);
            } else {
              reject(status);
            }
          }
        );
      });
      setBusinessSuggestions(predictions);
    } catch (error) {
      console.error('Error fetching business suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessSelect = async (event, value) => {
    if (!value || !placesService.current) return;

    setLoading(true);
    try {
      const placeDetails = await new Promise((resolve, reject) => {
        placesService.current.getDetails(
          { 
            placeId: value.place_id, 
            fields: ['name', 'types', 'formatted_address', 'business_status', 'rating', 'user_ratings_total'] 
          },
          (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(place);
            } else {
              reject(status);
            }
          }
        );
      });

      setSelectedBusiness(placeDetails);

      // No need to process categories anymore
    } catch (error) {
      console.error('Error fetching place details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRadiusChange = (event) => {
    setSearchRadius(event.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (selectedBusiness && keyword.trim()) {
      onSearch({
        businessName: selectedBusiness.name,
        keyword: keyword.trim(),
        location: selectedBusiness.formatted_address || selectedBusiness.vicinity,
        radius: searchRadius * 1000 // Convert km to meters
      });
    }
  };

  return (
    <StyledPaper elevation={3}>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="h6" sx={{ color: '#4568dc', mb: 2 }}>
          Find Your Business Ranking
        </Typography>
        
        <Autocomplete
          freeSolo
          options={businessSuggestions}
          getOptionLabel={(option) => option.description || ''}
          onChange={handleBusinessSelect}
          onInputChange={handleBusinessInputChange}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Your Business Name (India only)"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />

        <Box sx={{ width: '100%', mb: 2 }}>
          <TextField
            fullWidth
            label="Search Keyword"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            disabled={!selectedBusiness}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#4568dc',
                },
              },
              mb: 2
            }}
          />
          
          <FormControl fullWidth sx={{ mb: 1 }}>
            <InputLabel id="search-radius-label">Search Radius</InputLabel>
            <Select
              labelId="search-radius-label"
              value={searchRadius}
              label="Search Radius"
              onChange={handleRadiusChange}
              disabled={!selectedBusiness}
            >
              {[1, 2, 3, 5, 10, 15, 20].map((value) => (
                <MenuItem key={value} value={value}>{value} km</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {!selectedBusiness ? 'First select your business' : 'Enter a keyword to search for'}
          </Typography>
        </Box>

        <StyledButton 
          type="submit" 
          variant="contained"
          disabled={!selectedBusiness || keyword.trim() === '' || isSearching}
          fullWidth
          sx={{ position: 'relative' }}
        >
          {isSearching ? (
            <>
              <CircularProgress 
                size={24} 
                sx={{ 
                  color: 'white',
                  position: 'absolute',
                  left: 'calc(50% - 40px)',
                  top: 'calc(50% - 12px)'
                }} 
              />
              Searching...
            </>
          ) : 'Check Ranking'}
        </StyledButton>
      </Box>
    </StyledPaper>
  );
};

export default SearchForm;
